import { useEffect, useMemo, useState } from "react";
import { MONTHS_IT, api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Save, Baby, ListChecks, Check, Plus, Trash2, Activity, LogOut } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const sexes = [
  { v: "femmina", label: "Femmina", color: "#E5A8A0", bg: "#F9ECE9" },
  { v: "maschio", label: "Maschio", color: "#4A7496", bg: "#EBF3F8" },
  { v: "sorpresa", label: "Sorpresa", color: "#4E7363", bg: "#EAF0EC" },
];
const years = [2026, 2027, 2028];

export default function MyProfile({ open, onOpenChange }) {
  const { profile, wishlist, updateProfile, refresh, reset } = useProfile();
  const switchAccount = () => {
    if (!window.confirm("Vuoi uscire e accedere con un altro account? I dati di questo profilo restano salvati.")) return;
    localStorage.setItem("mb_onb_mode", "login");
    onOpenChange(false);
    reset();
  };
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [metric, setMetric] = useState("weight");
  const [series, setSeries] = useState([]);
  const [gform, setGform] = useState({ month: "", weight: "", length: "" });

  const gSex = profile?.sex === "maschio" ? "maschio" : "femmina";

  useEffect(() => {
    if (!open) return;
    api.get("/who-percentiles", { params: { sex: gSex, metric } })
      .then(({ data }) => setSeries(data.series || []))
      .catch(() => setSeries([]));
  }, [open, gSex, metric]);

  const records = useMemo(() => profile?.growth_records || [], [profile?.growth_records]);
  const gUnit = metric === "weight" ? "kg" : "cm";
  const gLabel = metric === "weight" ? "Peso" : "Lunghezza";

  const chartData = useMemo(() => series.map((s) => {
    const rec = records.find((r) => Number(r.month) === s.month);
    return { month: s.month, p50: s.p50, bimbo: rec ? rec[metric] ?? null : null };
  }), [series, records, metric]);

  const addRecord = async (e) => {
    e.preventDefault();
    if (gform.month === "") { toast.error("Seleziona il mese"); return; }
    if (!gform.weight && !gform.length) { toast.error("Inserisci peso o lunghezza"); return; }
    try {
      await api.post(`/profile/${profile.id}/growth`, {
        month: Number(gform.month),
        weight: gform.weight ? Number(gform.weight) : null,
        length: gform.length ? Number(gform.length) : null,
      });
      await refresh();
      setGform({ month: "", weight: "", length: "" });
      toast.success("Rilevamento aggiunto 🤍");
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const delRecord = async (month) => {
    try { await api.delete(`/profile/${profile.id}/growth/${month}`); await refresh(); }
    catch { toast.error("Errore"); }
  };

  useEffect(() => {
    if (profile && open) {
      setForm({
        name: profile.name || "",
        city: profile.city || "",
        day: String(profile.due_date?.day || ""),
        month: String(profile.due_date?.month || 1),
        year: String(profile.due_date?.year || years[0]),
        sex: profile.sex || "sorpresa",
        est_weight: profile.est_weight ?? "",
        est_length: profile.est_length ?? "",
        baby_name: profile.baby_name || "",
      });
    }
  }, [profile, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.city || !form.day) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    setSaving(true);
    try {
      await updateProfile({
        name: form.name,
        city: form.city,
        due_date: { day: Number(form.day), month: Number(form.month), year: Number(form.year) },
        sex: form.sex,
        est_weight: form.est_weight !== "" ? Number(form.est_weight) : null,
        est_length: form.est_length !== "" ? Number(form.est_length) : null,
        baby_name: form.baby_name.trim() || null,
      });
      toast.success("Dati aggiornati 🤍");
    } catch {
      toast.error("Salvataggio non riuscito. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  if (!profile || !form) return null;

  const sentAt = profile.list_sent_at
    ? new Date(profile.list_sent_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="my-profile-panel" className="w-full sm:max-w-md bg-[#FAF8F5] flex flex-col p-0">
        <SheetHeader className="p-6 pb-4 border-b border-[#EAE3DC]">
          <SheetTitle className="font-serif text-2xl text-[#2D2825] flex items-center gap-2">
            <Baby className="text-[#C87D75]" size={20} /> Il mio profilo
          </SheetTitle>
          <SheetDescription className="text-[#8C7F78]">
            Modifica i dati del tuo bambino e rivedi le tue liste.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#5C534E]">Nome *</Label>
                <Input data-testid="profile-name" value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="rounded-xl bg-white border-[#EAE3DC]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#5C534E]">Città *</Label>
                <Input data-testid="profile-city" value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  className="rounded-xl bg-white border-[#EAE3DC]" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">{form.year && new Date(Number(form.year), Number(form.month) - 1, Number(form.day || 1)) <= new Date() ? "Data di nascita *" : "Data prevista della nascita *"}</Label>
              <div className="grid grid-cols-3 gap-3">
                <select data-testid="profile-day" value={form.day}
                  onChange={(e) => set("day", e.target.value)}
                  className="rounded-xl bg-white border border-[#EAE3DC] h-10 px-3 text-sm">
                  <option value="">Giorno</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <select data-testid="profile-month" value={form.month}
                  onChange={(e) => set("month", e.target.value)}
                  className="rounded-xl bg-white border border-[#EAE3DC] h-10 px-3 text-sm capitalize">
                  {MONTHS_IT.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select data-testid="profile-year" value={form.year}
                  onChange={(e) => set("year", e.target.value)}
                  className="rounded-xl bg-white border border-[#EAE3DC] h-10 px-3 text-sm">
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">Sesso del bimbo</Label>
              <div className="grid grid-cols-3 gap-3">
                {sexes.map((s) => (
                  <button key={s.v} type="button" data-testid={`profile-sex-${s.v}`}
                    onClick={() => set("sex", s.v)}
                    className="rounded-xl h-10 text-sm font-medium border transition-all"
                    style={{
                      background: form.sex === s.v ? s.bg : "#fff",
                      borderColor: form.sex === s.v ? s.color : "#EAE3DC",
                      color: form.sex === s.v ? s.color : "#8C7F78",
                    }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">Nome del bimbo <span className="text-[#8C7F78] font-normal">(facoltativo)</span></Label>
              <Input data-testid="profile-baby-name" value={form.baby_name} maxLength={40}
                onChange={(e) => set("baby_name", e.target.value)} placeholder="es. Sofia"
                className="rounded-xl bg-white border-[#EAE3DC]" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#5C534E]">Peso stimato (kg)</Label>
                <Input data-testid="profile-weight" type="number" step="0.1" value={form.est_weight}
                  onChange={(e) => set("est_weight", e.target.value)} placeholder="facoltativo"
                  className="rounded-xl bg-white border-[#EAE3DC]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#5C534E]">Lunghezza stimata (cm)</Label>
                <Input data-testid="profile-length" type="number" step="0.1" value={form.est_length}
                  onChange={(e) => set("est_length", e.target.value)} placeholder="facoltativo"
                  className="rounded-xl bg-white border-[#EAE3DC]" />
              </div>
            </div>

            {profile.recommended_size?.label && (
              <p className="text-xs text-[#8C7F78]">
                Taglia consigliata attuale: <strong className="text-[#C87D75]">{profile.recommended_size.label}</strong>
              </p>
            )}

            <Button data-testid="profile-save-button" onClick={save} disabled={saving}
              className="w-full rounded-full bg-[#E5A8A0] hover:bg-[#C87D75] text-white h-11">
              {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={16} className="mr-2" /> Salva le modifiche</>}
            </Button>
            <button type="button" data-testid="profile-switch-account" onClick={switchAccount}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#EAE3DC] bg-white text-[#5C534E] hover:text-[#C87D75] hover:border-[#E5A8A0] h-11 text-sm transition-colors">
              <LogOut size={16} /> Esci / Cambia account
            </button>
          </div>

          <div className="pt-2 border-t border-[#EAE3DC]">
            <h3 className="font-display font-medium text-[#2D2825] flex items-center gap-2 mb-1 mt-4">
              <ListChecks className="text-[#4E7363]" size={18} /> La mia lista nascita
            </h3>
            {profile.list_sent ? (
              <div className="inline-flex items-center gap-1.5 text-xs text-[#4E7363] bg-[#EAF0EC] rounded-full px-3 py-1 mb-3">
                <Check size={13} /> Inviata allo shop{sentAt ? ` il ${sentAt}` : ""}
              </div>
            ) : (
              <p className="text-xs text-[#8C7F78] mb-3">Bozza non ancora inviata allo shop.</p>
            )}

            {wishlist.length === 0 ? (
              <p className="text-sm text-[#8C7F78]" data-testid="profile-list-empty">Nessun prodotto in lista.</p>
            ) : (
              <div className="space-y-2" data-testid="profile-list">
                {wishlist.map((it) => (
                  <div key={it.product_id} data-testid={`profile-list-item-${it.product_id}`}
                    className="flex gap-3 rounded-xl bg-white border border-[#EAE3DC] p-2.5 items-center">
                    {it.image && <img src={it.image} alt={it.title} className="w-12 h-12 rounded-lg object-cover" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#2D2825] line-clamp-1">{it.title}</div>
                      <div className="text-xs text-[#8C7F78]">
                        {it.size || "Taglia unica"} · x{it.quantity}{it.price ? ` · € ${it.price}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-[#EAE3DC]">
            <h3 className="font-display font-medium text-[#2D2825] flex items-center gap-2 mb-3 mt-4">
              <Activity className="text-[#4E7363]" size={18} /> Crescita del bambino
            </h3>
            <div className="inline-flex rounded-full border border-[#EAE3DC] bg-white p-1 mb-3">
              {[["weight", "Peso"], ["length", "Lunghezza"]].map(([k, l]) => (
                <button key={k} data-testid={`profile-metric-${k}`} onClick={() => setMetric(k)}
                  className="rounded-full px-4 py-1.5 text-sm transition-colors"
                  style={{ background: metric === k ? "#4E7363" : "transparent", color: metric === k ? "#fff" : "#5C534E" }}>
                  {l}
                </button>
              ))}
            </div>

            <div className="rounded-xl bg-white border border-[#EAE3DC] p-3" data-testid="profile-growth-chart">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE3DC" />
                  <XAxis dataKey="month" tick={{ fill: "#8C7F78", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#8C7F78", fontSize: 11 }} unit={gUnit} width={58} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EAE3DC", fontSize: 12 }}
                    formatter={(v, n) => [v != null ? `${v} ${gUnit}` : "-", n]}
                    labelFormatter={(l) => `Mese ${l}`} />
                  <Line type="monotone" dataKey="p50" name="Media OMS" stroke="#A7C4B5" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="bimbo" name={gLabel} stroke="#2D2825" strokeWidth={2.5}
                    connectNulls dot={{ r: 3, fill: "#2D2825" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <form onSubmit={addRecord} className="grid grid-cols-4 gap-2 mt-3">
              <select data-testid="profile-growth-month" value={gform.month}
                onChange={(e) => setGform((f) => ({ ...f, month: e.target.value }))}
                className="rounded-xl bg-white border border-[#EAE3DC] h-10 px-2 text-sm">
                <option value="">Mese</option>
                {Array.from({ length: 13 }, (_, i) => i).map((m) => <option key={m} value={m}>{m}m</option>)}
              </select>
              <Input data-testid="profile-growth-weight" type="number" step="0.1" placeholder="kg"
                value={gform.weight} onChange={(e) => setGform((f) => ({ ...f, weight: e.target.value }))}
                className="rounded-xl bg-white border-[#EAE3DC] h-10" />
              <Input data-testid="profile-growth-length" type="number" step="0.1" placeholder="cm"
                value={gform.length} onChange={(e) => setGform((f) => ({ ...f, length: e.target.value }))}
                className="rounded-xl bg-white border-[#EAE3DC] h-10" />
              <Button data-testid="profile-add-growth" type="submit"
                className="rounded-xl bg-[#4E7363] hover:bg-[#3d5a4e] text-white h-10 px-0">
                <Plus size={16} />
              </Button>
            </form>

            {records.length > 0 && (
              <div className="mt-3 space-y-1.5 max-h-36 overflow-y-auto">
                {records.map((r) => (
                  <div key={r.month} className="flex items-center justify-between rounded-lg bg-[#FAF8F5] border border-[#EAE3DC] px-3 py-1.5 text-sm">
                    <span className="text-[#5C534E]"><strong>{r.month}m</strong> · {r.weight ? `${r.weight}kg` : ""} {r.length ? `${r.length}cm` : ""}</span>
                    <button onClick={() => delRecord(r.month)} className="text-[#B85750] hover:opacity-70"
                      data-testid={`profile-delete-growth-${r.month}`}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
